import React, { useEffect, useState } from "react";
import api from "@/lib/api";

const ORIGINAL_TITLE = "My Crest Club · Special Orders";
const WARNING_TITLE = "(⚠) My Crest Club · Special Orders";

/**
 * IntegrationsBadge — at-a-glance status of the inbound reply-by-email loop.
 * Reads GET /api/health/integrations and shows LIVE / DORMANT with the config snapshot on click.
 * Also flips the browser tab title to a warning prefix when the loop is dormant.
 */
export default function IntegrationsBadge() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    api
      .get("/health/integrations")
      .then((r) => active && setData(r.data))
      .catch(() => active && setError(true));
    return () => {
      active = false;
    };
  }, []);

  // Mirror the live/dormant state into the document title for pinned-tab visibility.
  useEffect(() => {
    if (!data) return undefined;
    const live = data.webhook_secret_set && !!data.reply_to_domain;
    document.title = live ? ORIGINAL_TITLE : WARNING_TITLE;
    return () => {
      document.title = ORIGINAL_TITLE;
    };
  }, [data]);

  if (error) return null;

  const live = data && data.webhook_secret_set && !!data.reply_to_domain;
  let label = "Checking…";
  let dotCls = "bg-[#c8c4b3]";
  let pillCls = "border-[#E5E3D8] text-[#7A7A7A] bg-white";
  if (data) {
    if (live) {
      label = "Live";
      dotCls = "bg-[#1A422D]";
      pillCls = "border-[#1A422D] text-[#1A422D] bg-[#E9F0EB]";
    } else {
      label = "Dormant";
      dotCls = "bg-[#B59A5A]";
      pillCls = "border-[#B59A5A] text-[#8a6f33] bg-[#FBF6E8]";
    }
  }

  return (
    <div className="relative inline-block" data-testid="integrations-badge">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-2 px-3 py-1.5 text-[0.7rem] tracking-[0.18em] uppercase border font-semibold transition-colors ${pillCls}`}
      >
        <span className={`relative inline-flex items-center justify-center w-2 h-2 ${dotCls} rounded-full`}>
          {live && (
            <span className="absolute inline-flex w-full h-full rounded-full bg-[#1A422D] opacity-50 animate-ping" />
          )}
        </span>
        Reply Loop · {label}
      </button>

      {open && data && (
        <div className="absolute right-0 mt-2 w-80 z-30 surface-card p-5 fade-up" data-testid="integrations-badge-details">
          <div className="flex items-center justify-between">
            <div className="overline">Email Integrations</div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-xs text-[#7A7A7A] hover:text-[#1A422D] uppercase tracking-wider"
            >
              Close
            </button>
          </div>
          <h3 className="font-serif text-xl mt-1 text-[#1A1A1A]">
            {live ? "Reply-by-email is live" : "Reply loop is dormant"}
          </h3>
          <p className="text-xs text-[#7A7A7A] mt-1 leading-relaxed">
            {live
              ? "Members can reply directly to pro-shop emails — replies land back inside the order."
              : "Verify a Resend domain and set RESEND_WEBHOOK_SECRET + REPLY_TO_DOMAIN to enable."}
          </p>

          <dl className="mt-4 space-y-2 text-xs">
            <Row label="Sender" value={data.sender} mono />
            <Row label="Reply-To Domain" value={data.reply_to_domain} mono />
            <Row
              label="Webhook Secret"
              value={data.webhook_secret_set ? "Configured" : "Not set"}
              tone={data.webhook_secret_set ? "ok" : "warn"}
            />
            <Row label="Pro-shop Alerts" value={data.proshop_notify_to} mono />
            <Row label="Deploy Heartbeat" value={data.deploy_health_to} mono />
          </dl>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, mono, tone }) {
  let valueCls = "text-[#1A1A1A]";
  if (tone === "ok") valueCls = "text-[#1A422D]";
  else if (tone === "warn") valueCls = "text-[#8a6f33]";
  return (
    <div className="grid grid-cols-[110px_1fr] items-baseline gap-3">
      <dt className="label-elegant">{label}</dt>
      <dd className={`${mono ? "font-mono" : ""} ${valueCls} break-all`}>
        {value || <span className="text-[#a8a8a8]">—</span>}
      </dd>
    </div>
  );
}
